import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import dayjs from "dayjs";
import EventIcon from "@mui/icons-material/Event";
import ClearIcon from "@mui/icons-material/Clear";
import {
  InputAdornment,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  type TextFieldProps,
} from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useTranslation } from "react-i18next";
import {
  extractDateDigits,
  formatDateFilterDisplay,
  formatGermanDateDigits,
  inferDateFilterMode,
  isAnyDateRangeFilterValue,
  isClosedDateRangeFilterValue,
  isExactDateFilterValue,
  isOpenEndedBeforeFilterValue,
  isOpenEndedFromFilterValue,
  isValidDateDigitAtPosition,
  normalizeDateRangeValue,
  parseManualDateInput,
  parseDateDigits,
  sanitizeDateDigits,
  toDateRangeFilterValue,
  toExactDateFilterValue,
  toOpenEndedBeforeFilterValue,
  toOpenEndedFromFilterValue,
  type DateFilterMode,
} from "@/components/dateTime/genericDateFilterUtils";
import styles from "@/components/dateTime/genericDateRange.module.css";
import GenericCalendarPanel, {
  type CalendarPanelView,
  type GenericDateRangeSelectionMode,
  YEARS_PER_PAGE,
} from "@/components/dateTime/genericCalendarPanel";
import useSegmentedDateInput from "@/components/dateTime/useSegmentedDateInput";

export type { GenericDateRangeSelectionMode } from "@/components/dateTime/genericCalendarPanel";

interface Props {
  column?: any;
  selectionMode?: GenericDateRangeSelectionMode;
  value?: dayjs.Dayjs | null;
  onChange?: (value: dayjs.Dayjs | null) => void;
  onClear?: () => void;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  textFieldProps?: Omit<TextFieldProps, "value" | "onChange">;
}

const ALLOWED_INPUT_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
]);

const toPickerDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getInitialViewDate = (
  filterValue: unknown,
  committedRange: ReturnType<typeof normalizeDateRangeValue>,
): Date => {
  if (isAnyDateRangeFilterValue(filterValue)) {
    const rangeStart = toPickerDate(committedRange[0]);
    if (rangeStart) {
      return rangeStart;
    }
    const rangeEnd = toPickerDate(committedRange[1]);
    if (rangeEnd) {
      return rangeEnd;
    }
  }

  const singleDate = toPickerDate(filterValue);
  if (singleDate) {
    return singleDate;
  }

  return new Date();
};

function GenericDateRangeFn({
  column,
  selectionMode: requestedSelectionMode,
  value,
  onChange,
  onClear,
  disabled = false,
  clearable = true,
  className,
  textFieldProps,
}: Props) {
  const { t, i18n } = useTranslation();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const selectionMode =
    requestedSelectionMode ?? (column ? "range" : "single");
  const isSingleSelection = selectionMode === "single";
  const isStandalonePicker = !column;
  const isDisabled = disabled || Boolean(textFieldProps?.disabled);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [filterMode, setFilterMode] = useState<DateFilterMode>("pick");
  const [draftRange, setDraftRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [panelView, setPanelView] = useState<CalendarPanelView>("days");
  const [viewDate, setViewDate] = useState(() => new Date());
  const [yearsPageStart, setYearsPageStart] = useState(() =>
    Math.floor(new Date().getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE,
  );

  const filterValue = isStandalonePicker ? value : column?.getFilterValue();
  const isArrayFilterCommitted = isAnyDateRangeFilterValue(filterValue);
  const [inputValue, setInputValue] = useState(() =>
    formatDateFilterDisplay(filterValue, t),
  );

  const committedRange = useMemo(
    () => normalizeDateRangeValue(filterValue),
    [filterValue],
  );

  const committedExactDate = useMemo(() => {
    if (!isExactDateFilterValue(filterValue)) {
      return null;
    }
    return toPickerDate(filterValue);
  }, [filterValue]);

  const openEndedSelectedDate = useMemo(() => {
    if (filterMode === "from") {
      return toPickerDate(committedRange[0]);
    }
    if (filterMode === "before") {
      return toPickerDate(committedRange[1]);
    }
    return null;
  }, [committedRange, filterMode]);

  const isPickInProgress =
    filterMode === "pick" && draftRange[0] !== null && draftRange[1] === null;

  const pickCalendarStart = useMemo(() => {
    if (filterMode !== "pick") {
      return null;
    }
    if (draftRange[0]) {
      return draftRange[0];
    }
    if (committedExactDate) {
      return committedExactDate;
    }
    return null;
  }, [filterMode, draftRange, committedExactDate]);

  const pickCalendarEnd = useMemo(() => {
    if (filterMode !== "pick") {
      return null;
    }
    if (draftRange[1]) {
      return draftRange[1];
    }
    if (draftRange[0]) {
      return null;
    }
    if (committedExactDate) {
      return committedExactDate;
    }
    return null;
  }, [filterMode, draftRange, committedExactDate]);

  const resetPopoverUiState = useCallback(() => {
    setDraftRange([null, null]);
    setPanelView("days");
  }, []);

  useEffect(() => {
    setInputValue(formatDateFilterDisplay(filterValue, t));
    setFilterMode(inferDateFilterMode(filterValue));
  }, [filterValue, t]);

  useEffect(() => {
    if (!popoverOpen) {
      return;
    }

    setViewDate(getInitialViewDate(filterValue, committedRange));
    setPanelView("days");
    setYearsPageStart(
      Math.floor(
        getInitialViewDate(filterValue, committedRange).getFullYear() /
          YEARS_PER_PAGE,
      ) * YEARS_PER_PAGE,
    );
    setFilterMode(inferDateFilterMode(filterValue));

    if (isClosedDateRangeFilterValue(filterValue)) {
      setDraftRange([
        toPickerDate(committedRange[0]),
        toPickerDate(committedRange[1]),
      ]);
      return;
    }

    if (isOpenEndedFromFilterValue(filterValue)) {
      setDraftRange([toPickerDate(committedRange[0]), null]);
      return;
    }

    if (isOpenEndedBeforeFilterValue(filterValue)) {
      setDraftRange([null, toPickerDate(committedRange[1])]);
      return;
    }

    setDraftRange([null, null]);
  }, [popoverOpen, filterValue, committedRange]);

  const applyExactDate = (date: Date) => {
    if (isStandalonePicker) {
      onChange?.(dayjs(date));
    } else {
      column?.setFilterValue(toExactDateFilterValue(dayjs(date)));
    }
    setInputValue(dayjs(date).format("DD.MM.YYYY"));
    setPopoverOpen(false);
  };

  const applyDateRange = (start: Date, end: Date) => {
    const rangeValue = toDateRangeFilterValue(dayjs(start), dayjs(end));
    column?.setFilterValue(rangeValue);
    setInputValue(
      `${dayjs(start).format("DD.MM.YYYY")} – ${dayjs(end).format("DD.MM.YYYY")}`,
    );
    setPopoverOpen(false);
  };

  const applyOpenEndedFrom = (date: Date) => {
    column?.setFilterValue(toOpenEndedFromFilterValue(dayjs(date)));
    setInputValue(formatDateFilterDisplay(toOpenEndedFromFilterValue(dayjs(date)), t));
    setPopoverOpen(false);
  };

  const applyOpenEndedBefore = (date: Date) => {
    column?.setFilterValue(toOpenEndedBeforeFilterValue(dayjs(date)));
    setInputValue(
      formatDateFilterDisplay(toOpenEndedBeforeFilterValue(dayjs(date)), t),
    );
    setPopoverOpen(false);
  };

  const handlePickChange = (nextRange: [Date | null, Date | null]) => {
    const [nextStart, nextEnd] = nextRange;

    if (!nextStart) {
      setDraftRange([null, null]);
      return;
    }

    if (!nextEnd) {
      setDraftRange([nextStart, null]);
      return;
    }

    if (dayjs(nextStart).isSame(nextEnd, "day")) {
      applyExactDate(nextStart);
      return;
    }

    applyDateRange(nextStart, nextEnd);
  };

  const handleOpenEndedChange = (date: Date) => {
    if (filterMode === "from") {
      applyOpenEndedFrom(date);
      return;
    }

    if (filterMode === "before") {
      applyOpenEndedBefore(date);
    }
  };

  const handleModeChange = (
    _event: MouseEvent<HTMLElement>,
    nextMode: DateFilterMode | null,
  ) => {
    if (!nextMode) {
      return;
    }

    setFilterMode(nextMode);
    setDraftRange([null, null]);
    setPanelView("days");
  };

  const handleClear = (event?: MouseEvent) => {
    event?.stopPropagation();
    if (isStandalonePicker) {
      onChange?.(null);
      onClear?.();
    } else {
      column?.setFilterValue(undefined);
    }
    setInputValue("");
    setPopoverOpen(false);
  };

  const applyManualInput = (nextInputValue: string, shouldCommit = true) => {
    setInputValue(nextInputValue);

    if (nextInputValue.length === 0) {
      if (filterValue !== undefined) {
        if (isStandalonePicker) {
          onChange?.(null);
        } else {
          column?.setFilterValue(undefined);
        }
      }
      return;
    }

    if (!shouldCommit) {
      return;
    }

    const parsed = parseManualDateInput(nextInputValue);
    if (!parsed) {
      return;
    }

    const nextValue = toExactDateFilterValue(parsed);
    if (isStandalonePicker) {
      if (!dayjs(filterValue).isSame(parsed, "day")) {
        onChange?.(parsed);
      }
    } else if (filterValue !== nextValue) {
      column?.setFilterValue(nextValue);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isArrayFilterCommitted) {
      return;
    }

    const rawValue = event.target.value;
    if (/^\d*$/.test(rawValue)) {
      const digits = sanitizeDateDigits(rawValue);
      applyManualInput(formatGermanDateDigits(digits));
      return;
    }

    applyManualInput(rawValue.replace(/[^\d.]/g, "").slice(0, 10));
  };

  const segmentedDateInput = useSegmentedDateInput({
    inputValue,
    enabled: !isArrayFilterCommitted && !isDisabled,
    externalInputRef: textFieldProps?.inputRef,
    onValueChange: (nextValue, edit) => {
      applyManualInput(nextValue, edit.complete);
    },
  });

  const handleInputBlur = () => {
    segmentedDateInput.resetActiveSegment();
    if (isArrayFilterCommitted) {
      return;
    }

    const digits = extractDateDigits(inputValue);
    if (digits.length === 0) {
      return;
    }

    if (digits.length === 8 && parseDateDigits(digits)) {
      return;
    }

    setInputValue(formatDateFilterDisplay(filterValue, t));
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (isArrayFilterCommitted) {
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      return;
    }

    if (ALLOWED_INPUT_KEYS.has(event.key)) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const currentDigits = sanitizeDateDigits(extractDateDigits(inputValue));
    const nextDigit = Number(event.key);
    if (!isValidDateDigitAtPosition(currentDigits, nextDigit)) {
      event.preventDefault();
    }
  };

  const openPopover = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDisabled) {
      return;
    }
    setPopoverOpen(true);
  };

  const navigateSummaryDate = (date: dayjs.Dayjs) => {
    setViewDate(date.toDate());
    setPanelView("days");
    setYearsPageStart(
      Math.floor(date.year() / YEARS_PER_PAGE) * YEARS_PER_PAGE,
    );
  };

  const calendarLocale = i18n.language === "de" ? "de" : undefined;

  const showSummaryRow =
    panelView === "days" &&
    filterMode === "pick" &&
    !isPickInProgress &&
    (isExactDateFilterValue(filterValue) ||
      isClosedDateRangeFilterValue(filterValue));

  const committedSingleDate = isSingleSelection
    ? toPickerDate(filterValue)
    : null;

  return (
    <div
      ref={anchorRef}
      className={`${styles.triggerField} ${className ?? ""}`.trim()}
    >
      <TextField
        {...textFieldProps}
        inputRef={segmentedDateInput.inputRef}
        variant={
          textFieldProps?.variant ??
          (isSingleSelection ? "outlined" : "standard")
        }
        fullWidth={textFieldProps?.fullWidth ?? true}
        disabled={isDisabled}
        value={inputValue}
        placeholder={t("date_filter_placeholder")}
        onChange={handleInputChange}
        onClick={(event) => {
          segmentedDateInput.handleClick(event);
          textFieldProps?.onClick?.(event);
        }}
        onFocus={(event) => {
          segmentedDateInput.handleFocus(event);
          textFieldProps?.onFocus?.(event);
        }}
        onPaste={(event) => {
          segmentedDateInput.handlePaste(event);
          textFieldProps?.onPaste?.(event);
        }}
        onCut={(event) => {
          segmentedDateInput.handleCut(event);
          textFieldProps?.onCut?.(event);
        }}
        onBlur={(event) => {
          handleInputBlur();
          textFieldProps?.onBlur?.(event);
        }}
        onKeyDown={(event) => {
          const segmentHandled = segmentedDateInput.handleKeyDown(event);
          if (!segmentHandled) {
            handleInputKeyDown(event);
          }
          textFieldProps?.onKeyDown?.(event);
        }}
        inputProps={{
          ...textFieldProps?.inputProps,
          inputMode: "numeric",
          pattern: "[0-9.]*",
          readOnly: isArrayFilterCommitted,
          maxLength: 10,
        }}
        InputProps={{
          ...textFieldProps?.InputProps,
          endAdornment: (
            <InputAdornment position="end">
              {clearable && (inputValue || filterValue) && (
                <GenericButton
                  variant="icon"
                  color="iconBase"
                  aria-label={t("delete_filter")}
                  onClick={handleClear}
                  disabled={isDisabled}
                  onMouseDown={(event: any) => event.preventDefault()}
                >
                  <ClearIcon />
                </GenericButton>
              )}
              <GenericButton
                variant="icon"
                color="iconBase"
                aria-label={t(
                  isSingleSelection
                    ? "select_date"
                    : "date_range_filter_placeholder",
                )}
                onClick={openPopover}
                disabled={isDisabled}
                onMouseDown={(event: any) => event.preventDefault()}
              >
                <EventIcon />
              </GenericButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={() => setPopoverOpen(false)}
        TransitionProps={{ onExited: resetPopoverUiState }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { className: styles.popoverPaper },
        }}
      >
        {!isSingleSelection && (
          <ToggleButtonGroup
            exclusive
            value={filterMode}
            onChange={handleModeChange}
            fullWidth
            className={styles.modeToggleRow}
            size="small"
          >
            <ToggleButton value="pick" disableRipple>
              {t("date_filter_mode_pick")}
            </ToggleButton>
            <ToggleButton value="from" disableRipple>
              {t("from_date")}
            </ToggleButton>
            <ToggleButton value="before" disableRipple>
              {t("to_date")}
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        <div className={styles.calendarContainer}>
          <GenericCalendarPanel
            panelView={panelView}
            setPanelView={setPanelView}
            viewDate={viewDate}
            setViewDate={setViewDate}
            yearsPageStart={yearsPageStart}
            setYearsPageStart={setYearsPageStart}
            locale={calendarLocale}
            filterMode={filterMode}
            selectionMode={selectionMode}
            selected={
              isSingleSelection ? committedSingleDate : openEndedSelectedDate
            }
            startDate={filterMode === "pick" ? pickCalendarStart : null}
            endDate={filterMode === "pick" ? pickCalendarEnd : null}
            onPickChange={handlePickChange}
            onOpenEndedChange={handleOpenEndedChange}
            onSingleDateChange={applyExactDate}
          />

          {showSummaryRow && (
            <div className={styles.summaryRow}>
              {isExactDateFilterValue(filterValue) && (
                <button
                  type="button"
                  className={styles.summaryItem}
                  onClick={() =>
                    navigateSummaryDate(dayjs(filterValue as string))
                  }
                >
                  {t("on_date")}:{" "}
                  {dayjs(filterValue as string).format("DD.MM.YYYY")}
                </button>
              )}
              {isClosedDateRangeFilterValue(filterValue) && (
                <>
                  <button
                    type="button"
                    className={styles.summaryItem}
                    onClick={() => {
                      if (committedRange[0]) {
                        navigateSummaryDate(committedRange[0]);
                      }
                    }}
                  >
                    {t("from_date")}:{" "}
                    {dayjs(committedRange[0]).format("DD.MM.YYYY")}
                  </button>
                  <button
                    type="button"
                    className={styles.summaryItem}
                    onClick={() => {
                      if (committedRange[1]) {
                        navigateSummaryDate(committedRange[1]);
                      }
                    }}
                  >
                    {t("to_date")}:{" "}
                    {dayjs(committedRange[1]).format("DD.MM.YYYY")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

/**
 * MRT invokes `columnDef.Filter({ column })` directly, so keep this adapter as
 * the value assigned to `Filter`. For form fields, omit `column` and use
 * `selectionMode="single"`, `value`, and `onChange` as controlled props.
 */
export default function GenericDateRange(props: Props) {
  return <GenericDateRangeFn {...props} />;
}
